import { Component, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { TrendingUp, TrendingDown, Circle, Pause, Play } from "lucide-react";
import { api } from "../lib/api";

/**
 * Site-wide market ticker. Restored 2026-09-23 against a real backend (GET /api/market/tickers):
 * every value is either a real, sourced reading (ERCOT, EIA) or an honest "unavailable"
 * placeholder -- never a fabricated number (QA-23). See backend/content/market.py, ercot.py, eia.py.
 * Design tokens (--rc-ink / --rc-green-600) per Design Specification Section 3.1/3.6.
 *
 * Freshness (Owner directive 2026-10-11): the tape never says "Live" and never prints a time of day. An item
 * with current data shows no freshness text at all; only a genuinely delayed item (any known non-live freshness,
 * e.g. Henry Hub) carries a small neutral "Delayed" marker, with the as-of DATE (Central) when the API supplies one.
 * freshness "stale" / "unavailable" items are hidden entirely, never shown as a number. The API still sends
 * source_name / source_url / as_of / is_live; the source fields are intentionally ignored (no attribution on the tape).
 *
 * Controls (Owner directive 2026-10-11): the tape is moved by JS (one translateX written to a ref, no per-frame
 * React renders) so a person can drag it (mouse / touch / pen, direct manipulation, no momentum). It pauses on hover,
 * keyboard focus, touch-down / drag and via a visible Pause/Play button, resumes a few seconds after release, loops
 * infinitely (the offset wraps modulo one copy's width), honors prefers-reduced-motion (starts paused, still
 * draggable) and scrolls with the arrow keys when focused. No dependencies.
 */
const CT_ZONE = "America/Chicago";

function ctParts(date, opts) {
  const out = {};
  new Intl.DateTimeFormat("en-US", { timeZone: CT_ZONE, ...opts }).formatToParts(date).forEach(p => { out[p.type] = p.value; });
  return out;
}

/** Plain-words as-of DATE only, never a time: "as of Oct 6". A date-only / day-precision value is a calendar date with
 *  no time zone conversion (an EIA price date is a date, not an instant); a timestamp becomes its Central calendar date.
 *  Returns "" when as_of is missing or unparseable (callers then say nothing rather than guess). */
export function formatAsOfDate(asOf, precision) {
  if (typeof asOf !== "string" || !asOf.trim()) return "";
  const raw = asOf.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw) || precision === "day") {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
    if (!m) return "";
    const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], 12));
    if (Number.isNaN(d.getTime())) return "";
    return `as of ${new Intl.DateTimeFormat("en-US", { timeZone: "UTC", month: "short", day: "numeric" }).format(d)}`;
  }
  const when = new Date(raw);
  if (Number.isNaN(when.getTime())) return "";
  const dm = ctParts(when, { month: "short", day: "numeric" });
  return `as of ${dm.month} ${dm.day}`;
}

const isStr = (v) => typeof v === "string";

/**
 * Defensive normalisation of one API item. The tape is mounted on every public page OUTSIDE the per-page error
 * boundary, so a malformed item (an object where text belongs, NaN, a bare string) must never reach render, where
 * it would throw and blank the whole site. Returns null for anything that cannot be shown honestly: no label, or an
 * "available" item without a real reading. A bad optional field is dropped; it is never printed as "NaN".
 */
export function normalizeItem(raw, i = 0) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  if (!isStr(raw.label) || !raw.label.trim()) return null;
  const available = !!raw.available; // same truthiness the render always used: missing means not available
  const out = {
    id: isStr(raw.id) && raw.id ? raw.id : `item-${i}`,
    label: raw.label,
    unit: isStr(raw.unit) ? raw.unit : "",
    note: isStr(raw.note) ? raw.note : "",
    available,
    direction: raw.direction === "up" ? "up" : "down",
    change_pct: typeof raw.change_pct === "number" && Number.isFinite(raw.change_pct) ? raw.change_pct : undefined,
    as_of: isStr(raw.as_of) ? raw.as_of : null,
    as_of_precision: isStr(raw.as_of_precision) ? raw.as_of_precision : undefined,
    freshness: raw.freshness ? (isStr(raw.freshness) ? raw.freshness : "delayed") : undefined,
    is_live: raw.is_live === true,
  };
  if (available) {
    const v = raw.value;
    const okNum = typeof v === "number" && Number.isFinite(v);
    const okStr = isStr(v) && v.trim() !== "";
    if (!okNum && !okStr) return null; // "available" with no real reading: hide it, never show a blank or a guess
    out.value = v;
  }
  return out;
}

/** A render fault inside the tape hides the tape only; it never takes the page down with it. */
class TickerBoundary extends Component {
  constructor(props) { super(props); this.state = { failed: false }; }
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch() {}
  render() { return this.state.failed ? null : this.props.children; }
}

/** Items the API says are stale/unavailable are hidden outright. */
export function isHiddenItem(t) {
  return !t || t.freshness === "stale" || t.freshness === "unavailable";
}

export default function TickerTape() {
  return <TickerBoundary><TickerTapeInner /></TickerBoundary>;
}

const SPEED_PX_PER_S = 40;      // auto-scroll speed
const RESUME_DELAY_MS = 3000;   // auto-scroll resumes this long after a drag / touch is released
const KEY_STEP_PX = 96;         // one arrow-key press

const prefersReducedMotion = () =>
  typeof window !== "undefined" && typeof window.matchMedia === "function" && !!window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Keyboard-originated focus only: a mouse press that merely focuses the strip must not freeze it afterwards. */
function isKeyboardFocus(el) {
  try { return el.matches(":focus-visible"); } catch (e) { return true; }
}

function TickerTapeInner() {
  const [tickers, setTickers] = useState([]);
  const [userPaused, setUserPaused] = useState(prefersReducedMotion);
  const [hovering, setHovering] = useState(false);
  const [focused, setFocused] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [holdoff, setHoldoff] = useState(false);
  const [copies, setCopies] = useState(3);

  const viewportRef = useRef(null);
  const trackRef = useRef(null);
  const groupRef = useRef(null);
  const offsetRef = useRef(0);       // distance the tape has moved left, kept in [0, setWidth)
  const setWidthRef = useRef(0);     // pixel width of ONE copy of the items (the loop period)
  const dragRef = useRef(null);      // { id, startX, startOffset } while a pointer is down
  const holdTimer = useRef(null);

  useEffect(() => {
    let mounted = true;
    const load = () => api.get("/market/tickers").then(r => { if (mounted) setTickers(Array.isArray(r && r.data && r.data.tickers) ? r.data.tickers : []); }).catch(() => {});
    load();
    // TickerTape is mounted on every public page (App.js PublicShell), so a real 15s interval here
    // would leak into every test in the whole frontend suite that renders any public route -- not
    // just this component's own tests -- accumulating pending timers/fetches across a test file and
    // pushing unrelated later tests over their timeout (see src/setupTests.js's comment on exactly
    // this cascade shape). No interval in the test environment; polling is a freshness nicety, not
    // something a test needs to exercise here (a dedicated test can still opt in with fake timers).
    if (process.env.NODE_ENV === "test") return () => { mounted = false; };
    const t = setInterval(load, 15000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  const visible = tickers.map((t, i) => normalizeItem(t, i)).filter(t => t && !isHiddenItem(t));
  const count = visible.length;

  /** Wrap into [0, setWidth) so dragging past either end is seamless (every copy is identical), then paint. */
  const apply = useCallback((raw) => {
    const w = setWidthRef.current;
    const o = w > 0 ? ((raw % w) + w) % w : raw;
    offsetRef.current = o;
    if (trackRef.current) trackRef.current.style.transform = `translate3d(${-o}px, 0, 0)`;
  }, []);

  // Measure one copy and the viewport; render enough copies that the strip is always full (viewport + one copy of slack).
  const measure = useCallback(() => {
    const g = groupRef.current;
    const v = viewportRef.current;
    if (!g || !v) return;
    const w = g.offsetWidth || 0;
    setWidthRef.current = w;
    const need = w > 0 ? Math.max(3, Math.ceil((v.offsetWidth || 0) / w) + 2) : 3;
    setCopies(c => (c === need ? c : need));
    apply(offsetRef.current);
  }, [apply]);

  useLayoutEffect(() => {
    measure();
    const g = groupRef.current;
    let ro;
    if (typeof ResizeObserver !== "undefined" && g) {
      ro = new ResizeObserver(measure);
      ro.observe(g);
      if (viewportRef.current) ro.observe(viewportRef.current);
    }
    window.addEventListener("resize", measure);
    return () => { if (ro) ro.disconnect(); window.removeEventListener("resize", measure); };
  }, [measure, count]);

  // Reduced motion switched on mid-session: stop. (Switching it off never auto-starts; the person presses Play.)
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = (e) => { if (e.matches) setUserPaused(true); };
    if (mq && mq.addEventListener) { mq.addEventListener("change", onChange); return () => mq.removeEventListener("change", onChange); }
    return undefined;
  }, []);

  const running = count > 0 && !userPaused && !hovering && !focused && !dragging && !holdoff;

  useEffect(() => {
    if (!running || typeof requestAnimationFrame !== "function") return undefined;
    let raf;
    let last = null;
    const step = (ts) => {
      if (last !== null) apply(offsetRef.current + (Math.min(ts - last, 100) / 1000) * SPEED_PX_PER_S);
      last = ts;
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [running, apply]);

  useEffect(() => () => clearTimeout(holdTimer.current), []);

  // The tape unmounts when the API reports nothing usable. A node that is gone can never deliver pointerleave / pointerup /
  // blur, so hover, focus and drag state would otherwise survive and freeze the tape when data returns (QA, real Chrome).
  useEffect(() => {
    if (count > 0) return;
    clearTimeout(holdTimer.current);
    dragRef.current = null;
    setHovering(false);
    setFocused(false);
    setDragging(false);
    setHoldoff(false);
  }, [count]);

  const release = () => {
    if (!dragRef.current) return;
    dragRef.current = null;
    setDragging(false);
    clearTimeout(holdTimer.current);
    setHoldoff(true);
    holdTimer.current = setTimeout(() => setHoldoff(false), RESUME_DELAY_MS);
  };

  const onPointerDown = (e) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    if (dragRef.current) return;
    clearTimeout(holdTimer.current);
    dragRef.current = { id: e.pointerId, startX: e.clientX, startOffset: offsetRef.current };
    setDragging(true);
    setHoldoff(false);
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) { /* unsupported or synthetic pointer */ }
  };
  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d || d.id !== e.pointerId) return;
    apply(d.startOffset - (e.clientX - d.startX)); // direct manipulation: the tape stays under the pointer, no momentum
  };
  const onPointerUp = (e) => {
    const d = dragRef.current;
    if (!d || d.id !== e.pointerId) return;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (err) { /* already released */ }
    release();
  };
  const onKeyDown = (e) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();
    apply(offsetRef.current + (e.key === "ArrowRight" ? KEY_STEP_PX : -KEY_STEP_PX));
  };

  if (!count) return null;

  const groups = [];
  for (let c = 0; c < copies; c += 1) {
    groups.push(
      <div key={`copy-${c}`} ref={c === 0 ? groupRef : undefined} className="flex shrink-0" data-testid={`ticker-copy-${c}`}>
        {visible.map((t, i) => {
          const idx = c * count + i;
          return <TickerItem key={`${t.id}-${idx}`} t={t} testId={`ticker-${t.id}-${idx}`} isCopy={c > 0} />;
        })}
      </div>
    );
  }

  return (
    <div className="bg-[var(--rc-ink)] text-white border-b border-[var(--rc-ink)] overflow-hidden" data-testid="ticker-tape">
      <div className="flex items-stretch">
        <div className="shrink-0 bg-[var(--rc-green-600)] text-black px-4 py-2.5 flex items-center gap-2 text-xs font-bold uppercase tracking-wider z-10">
          <Circle className="w-2 h-2 fill-black motion-safe:animate-pulse" aria-hidden="true" /> Texas energy
        </div>
        <div
          ref={viewportRef}
          className="ticker-viewport flex-1 overflow-hidden"
          data-testid="ticker-viewport"
          data-dragging={dragging ? "true" : "false"}
          role="group"
          aria-roledescription="scrolling ticker"
          aria-label="Texas energy market ticker. Drag, swipe, or use the left and right arrow keys to scroll."
          tabIndex={0}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onLostPointerCapture={onPointerUp}
          onPointerEnter={(e) => { if (e.pointerType !== "touch") setHovering(true); }}
          onPointerLeave={() => setHovering(false)}
          onFocus={(e) => { if (isKeyboardFocus(e.currentTarget)) setFocused(true); }}
          onBlur={() => setFocused(false)}
          onKeyDown={onKeyDown}
        >
          <div ref={trackRef} className="ticker-track" data-testid="ticker-track">
            {groups}
          </div>
        </div>
        <button
          type="button"
          className="shrink-0 w-11 flex items-center justify-center bg-[var(--rc-ink)] text-white border-l border-white/10 hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-white z-10"
          data-testid="ticker-toggle"
          aria-pressed={userPaused}
          aria-label="Pause ticker scrolling"
          onClick={() => setUserPaused(p => !p)}
        >
          {userPaused ? <Play className="w-4 h-4" aria-hidden="true" /> : <Pause className="w-4 h-4" aria-hidden="true" />}
        </button>
      </div>
    </div>
  );
}

/** Small neutral marker, shown ONLY for a genuinely delayed item (any known non-live freshness). Never "Live", never a time. */
function DelayedMarker({ t, testId }) {
  if (!t.freshness) return null; // older API: make no freshness claim either way
  if (t.freshness === "live" && t.is_live === true) return null; // current data: no badge, no timestamp
  const asOf = formatAsOfDate(t.as_of, t.as_of_precision);
  return (
    <span className="text-[10px] font-medium leading-4 text-zinc-300 border border-white/20 rounded px-1.5" data-testid={`${testId}-freshness`} data-freshness="delayed">
      Delayed{asOf ? `, ${asOf}` : ""}
    </span>
  );
}

function TickerItem({ t, testId, isCopy }) {
  const wrap = "flex items-center px-6 py-3 shrink-0 border-r border-white/10";
  // The tape renders several copies for the seamless loop; only the first copy is exposed to
  // assistive tech, so screen readers do not meet every item more than once.
  const copyProps = isCopy ? { "aria-hidden": "true" } : {};
  if (!t.available) {
    return (
      <div className={wrap} data-testid={testId} {...copyProps}>
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-zinc-300">{t.label}</span>
          <span className="text-xs text-zinc-500" data-testid={`${testId}-unavailable`}>Data unavailable</span>
        </div>
      </div>
    );
  }
  const hasChange = typeof t.change_pct === "number";
  const up = t.direction === "up";
  return (
    <div className={wrap} data-testid={testId} {...copyProps}>
      <div className="flex items-center gap-3 whitespace-nowrap">
        <span className="text-sm font-medium text-zinc-300">{t.label}</span>
        <span className="text-sm font-semibold tabular-nums">
          {t.value}{t.unit ? <span className="text-xs text-zinc-500"> {t.unit}</span> : null}
        </span>
        {hasChange && (
          <span className={`flex items-center gap-1 text-xs font-bold tabular-nums ${up ? "text-emerald-400" : "text-rose-400"}`}>
            {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {up ? "+" : ""}{t.change_pct}%
          </span>
        )}
        {t.note && <span className="text-[10px] uppercase tracking-wider text-[var(--rc-green-400)]">{t.note}</span>}
        <DelayedMarker t={t} testId={testId} />
      </div>
    </div>
  );
}
